/**
 * Утилита для логирования событий админ-панели на бэкенд
 *
 * Логи отправляются на эндпоинт /api/auth/log и записываются в /logs/auth.log
 * с маркировкой [Frontend] и [ADMIN]
 */

const API_URL = import.meta.env.VITE_API_URL || '/api';

export type AdminEventType = 'admin_console_access' | 'admin_logout' | 'admin_action';

export interface AdminLogEvent {
  event_type: AdminEventType;
  email: string;
  success: boolean;
  action?: string;
  error_message?: string | null;
  ip_address?: string | null;
}

/**
 * Отправить событие админ-панели на логирование
 *
 * @param event - Данные события для логирования
 *
 * @example
 * // Логирование входа в админ-панель
 * await logAdminEvent({
 *   event_type: 'admin_login',
 *   email: 'admin@example.com',
 *   success: true
 * });
 *
 * @example
 * // Логирование действия в админ-панели
 * await logAdminEvent({
 *   event_type: 'admin_action',
 *   email: 'admin@example.com',
 *   success: true,
 *   action: 'user_delete'
 * });
 */
export async function logAdminEvent(event: AdminLogEvent): Promise<void> {
  try {
    await fetch(`${API_URL}/auth/log`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(event),
    });
    // Игнорируем ошибки логирования - они не должны влиять на работу приложения
  } catch (error) {
    // Тихо игнорируем ошибки логирования в консоль
    console.warn('[AdminLogger] Failed to send log event:', error);
  }
}

/**
 * Helper функции для конкретных событий
 */

export const logAdminConsoleAccess = (email: string) =>
  logAdminEvent({ event_type: 'admin_console_access', email, success: true });

export const logAdminConsoleAccessFailure = (email: string, errorMessage?: string) =>
  logAdminEvent({ event_type: 'admin_console_access', email, success: false, error_message: errorMessage });

export const logAdminLogout = (email: string) =>
  logAdminEvent({ event_type: 'admin_logout', email, success: true });

export const logAdminAction = (email: string, action: string, success: boolean, errorMessage?: string) =>
  logAdminEvent({
    event_type: 'admin_action',
    email,
    success,
    action,
    error_message: errorMessage
  });
